/**
 * The asset's own relations: an offer is built from other assets, and any asset can be part of a platform. Incoming
 * relations belong to the other asset and are edited there. Find an asset by name (from two characters), choose the
 * relation, and **Save** replaces the list under the revision rule.
 */

import { useState } from 'react';
import { Plus, Search, X } from 'lucide-react';

import { isStaleRevision, useAssets, useSaveRelations } from '@/api/queries';
import type { AssetCard, AssetOut, RelationType } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { buttonState } from '@/ui/ActionButton';
import { SaveBar } from '@/ui/SaveBar';
import { FormError } from '@/ui/StateCard';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

interface RelationDraft {
  to_asset_id: string;
  type: RelationType;
  name: string;
  kind: string;
}

const TYPE_WORDS: Record<RelationType, string> = { built_from: 'Built from', part_of: 'Part of' };

export function relationDrafts(asset: AssetOut): RelationDraft[] {
  return asset.relations
    .filter((r) => r.direction === 'outgoing')
    .map((r) => ({ to_asset_id: r.asset.id, type: r.type as RelationType, name: r.asset.name, kind: r.asset.kind }));
}

/** The relation types an asset may start: only an offer is built from others. */
export function typesFor(kind: string): RelationType[] {
  return kind === 'offer' ? ['built_from', 'part_of'] : ['part_of'];
}

/** Whether `candidate` can be the other end: not the asset itself, not chosen already, a platform for part of. */
export function canRelate(asset: AssetOut, chosen: RelationDraft[], type: RelationType, candidate: AssetCard): boolean {
  if (candidate.id === asset.id || chosen.some((c) => c.to_asset_id === candidate.id)) return false;
  return type !== 'part_of' || candidate.kind === 'platform';
}

const keyOf = (list: RelationDraft[]) => JSON.stringify(list.map((r) => [r.to_asset_id, r.type]));

function Finder({ asset, chosen, type, onAdd }: { asset: AssetOut; chosen: RelationDraft[]; type: RelationType; onAdd: (card: AssetCard) => void }) {
  const [q, setQ] = useState('');
  const [text, setText] = useDebouncedSearch(q, (patch) => setQ(patch.q));
  const found = useAssets({ q, page_size: 10, ...(type === 'part_of' ? { kind: ['platform'] } : {}) }, { enabled: q.length >= 2 });
  const rows = (found.data?.assets ?? []).filter((c) => canRelate(asset, chosen, type, c));
  return (
    <>
      <div className="search-field">
        <Search aria-hidden="true" />
        <input type="search" value={text} placeholder="Find an asset by name" aria-label="Find an asset to relate" onChange={(e) => setText(e.target.value)} />
      </div>
      <div aria-live="polite">
        {q.length >= 2 && found.data && rows.length === 0 && <p className="hint">No asset to relate matches.</p>}
        {rows.length > 0 && (
          <ul className="relation-results" aria-label="Assets found">
            {rows.map((c) => (
              <li key={c.id}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { onAdd(c); setText(''); }}>
                  <Plus aria-hidden="true" /> {c.name} <Badge variant="kind" value={c.kind} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

export function RelationsEditor({ asset, onStale }: { asset: AssetOut; onStale: () => void }) {
  const save = useSaveRelations(asset.id);
  const initial = relationDrafts(asset);
  const [draft, setDraft] = useState<RelationDraft[]>(initial);
  const types = typesFor(asset.kind);
  const [type, setType] = useState<RelationType>(types[0] as RelationType);
  const changed = keyOf(draft) !== keyOf(initial);

  const submit = () =>
    save.mutate(
      { revision: asset.revision, relations: draft.map(({ to_asset_id, type: t }) => ({ to_asset_id, type: t })) },
      { onError: (error) => isStaleRevision(error) && onStale() },
    );

  return (
    <section className="card asset-editor-relations" aria-labelledby="editor-relations-title">
      <div className="card-body stack-md">
        <h2 id="editor-relations-title">Related assets</h2>
        <FormError error={isStaleRevision(save.error) ? null : save.error} />
        {draft.length === 0 ? (
          <p className="hint">No related assets.</p>
        ) : (
          <ul className="relation-list" aria-label="Related assets">
            {draft.map((r) => (
              <li key={`${r.type}-${r.to_asset_id}`}>
                <span>{TYPE_WORDS[r.type]}</span> <b>{r.name}</b> <Badge variant="kind" value={r.kind} />
                <button type="button" className="icon-btn" aria-label={`Remove ${r.name}`} title="Remove" onClick={() => setDraft(draft.filter((d) => d !== r))}>
                  <X aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="relation-add">
          <div className="field">
            <label htmlFor="relation-type">Relation</label>
            <select id="relation-type" value={type} onChange={(e) => setType(e.target.value as RelationType)}>
              {types.map((t) => <option key={t} value={t}>{TYPE_WORDS[t]}</option>)}
            </select>
          </div>
          <Finder asset={asset} chosen={draft} type={type} onAdd={(c) => setDraft([...draft, { to_asset_id: c.id, type, name: c.name, kind: c.kind }])} />
        </div>
        <SaveBar changed={changed} state={buttonState(save)} onReset={() => setDraft(initial)} onSave={submit} />
      </div>
    </section>
  );
}
